import React, { useState, useEffect } from "react";
import { getProceedingByCandidate } from "../../services/proceedings.service.ts";
import { getFilesByProceeding } from "../../services/candidate.service.ts";
import DocumentsViewer from "../profile/DocumentsViewer.tsx";
import Loader from "../Loader.tsx";

interface DocumentWithFiles {
  id: number;
  name: string;
  obligatory: boolean;
  fields: any[];
  files: any[];
}

interface CandidateDocumentsViewerProps {
  candidateId: number;
  positionId: number;
}

const CandidateDocumentsViewer: React.FC<CandidateDocumentsViewerProps> = ({
  candidateId,
  positionId,
}) => {
  const [loading, setLoading] = useState(true);
  const [documentsWithFiles, setDocumentsWithFiles] = useState<DocumentWithFiles[]>([]);

  useEffect(() => {
    if (candidateId && positionId) {
      loadDocuments();
    }
  }, [candidateId, positionId]);

  const loadDocuments = async () => {
    if (!candidateId) return;

    setLoading(true);
    try {
      // Obtener el proceeding del candidate
      const proceeding = await getProceedingByCandidate(candidateId);
      
      if (!proceeding?.proceeding?.id) {
        setDocumentsWithFiles([]);
        setLoading(false);
        return;
      }

      // Obtener todos los file proceedings del candidate
      const fileProceedings = await getFilesByProceeding(proceeding.proceeding.id);

      // Agrupar por tipo de documento
      const documentsMap = new Map<number, DocumentWithFiles>();

      if (fileProceedings && fileProceedings.length > 0) {
        for (const fileProc of fileProceedings) {
          const docTypeId = fileProc.documentType?.id;
          if (!docTypeId) continue;

          // Ocultar "Foto colaborador"
          if (fileProc.documentType?.name === "Foto colaborador") {
            continue;
          }

          if (!documentsMap.has(docTypeId)) {
            documentsMap.set(docTypeId, {
              id: docTypeId,
              name: fileProc.documentType.name,
              obligatory: fileProc.documentType.obligatory || false,
              fields: fileProc.documentType.fields || [],
              files: [],
            });
          }

          const doc = documentsMap.get(docTypeId)!;
          doc.files.push(fileProc);
        }
      }

      // Convertir a array y filtrar solo los que tienen archivos
      const docsArray = Array.from(documentsMap.values()).filter(
        (doc) => doc.files.length > 0
      );

      setDocumentsWithFiles(docsArray);
    } catch (error) {
      console.error("Error loading documents:", error);
      setDocumentsWithFiles([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date: string) => {
    const newDate = new Date(date);
    return `${newDate.getDate()}/${
      newDate.getMonth() + 1
    }/${newDate.getFullYear()}`;
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <Loader isLoading={true} />
      </div>
    );
  }

  if (documentsWithFiles.length === 0) {
    return (
      <div className="alert alert-info mb-3">
        <i className="fas fa-info-circle me-2"></i>
        No hay documentos subidos para mostrar.
      </div>
    );
  }

  return (
    <div className="mt-3">
      <h6 className="mb-2 text-dark" style={{ fontSize: "0.9rem" }}>
        <i className="fas fa-folder-open me-2"></i>
        Documentos Subidos
      </h6>

      <div className="row g-2">
        {documentsWithFiles.map((doc) => {
          const latestFile = doc.files[doc.files.length - 1];
          const uploadDate = latestFile?.updateAt || latestFile?.createdAt;

          return (
            <div key={doc.id} className="col-md-6 col-lg-4">
              <div
                className="card border h-100"
                style={{ transition: "all 0.2s ease" }}
              >
                <div className="card-header bg-light d-flex justify-content-between align-items-center py-1 px-2">
                  <div className="d-flex align-items-center flex-grow-1" style={{ minWidth: 0 }}>
                    <i className="fa fa-file-alt me-1 text-primary" style={{ fontSize: "0.75rem" }}></i>
                    <small
                      className="fw-bold text-dark text-truncate"
                      style={{ fontSize: "0.75rem" }}
                      title={doc.name}
                    >
                      {doc.name}
                    </small>
                  </div>
                  <div className="d-flex align-items-center gap-1 ms-2">
                    <span className="badge bg-success" style={{ fontSize: "0.65rem", padding: "0.2rem 0.4rem" }}>
                      <i className="fa fa-check"></i>
                    </span>
                    {doc.obligatory && (
                      <span className="badge bg-danger" style={{ fontSize: "0.65rem", padding: "0.2rem 0.4rem" }}>
                        Obligatorio
                      </span>
                    )}
                  </div>
                </div>
                <div className="card-body p-2">
                  {/* Información de campos */}
                  {latestFile?.fields && latestFile.fields.length > 0 && (
                    <div className="mb-1">
                      {latestFile.fields.map((field: any, index: number) => (
                        <div key={index} className="mb-1">
                          <label className="text-muted" style={{ fontSize: "0.7rem", marginBottom: "0.1rem" }}>
                            {field.field || field.name}:
                          </label>
                          <div className="bg-light p-1 rounded" style={{ fontSize: "0.75rem" }}>
                            {field.value || ""}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Vista de documentos */}
                  <div className="mb-1" style={{ minHeight: "100px", maxHeight: "200px", overflow: "auto" }}>
                    <DocumentsViewer fileData={doc.files} />
                  </div>

                  {/* Información de subida */}
                  {uploadDate && (
                    <div className="alert alert-success mb-0 py-1 px-2" style={{ fontSize: "0.7rem" }}>
                      <i className="fa fa-calendar me-1"></i>
                      {formatDate(uploadDate)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CandidateDocumentsViewer;

